import { CoreClient } from '@ingenico/api-sdk-core/common';

import type {
  ConfigurationModuleResourceReadBffV2Api,
  ConfigurationModuleSnapshotResourceParameterSetSnapshotReadBffV2Api,
  ConfigurationModuleResourceReadV2Api,
} from '@ingenico/api-sdk-device-management-private-v2';
import {
  ApplicationResourceListResponseV2Api,
  downloadAssetByIdV2,
  getApplicationVersionTemplatesByApplicationVersionIdV2,
  getComputedConfigurationModuleSnapshotByIdBffV2,
  getComputedParameterSetSnapshotByIdBffV2,
  getConfigurationByIdBffV2,
  getConfigurationModuleByIdBffV2,
  getConfigurationModuleSnapshotByIdBffV2,
  getDeployedComputedConfigurationByDeviceIdBffV2,
  getEnvironmentByOrganizationIdV2,
  getKeySetReferenceV2,
  getOrganizationKeySetsV2,
  getSystemPackageVersionTemplatesBySystemPackageVersionIdV2,
  getTemplateByIdV2,
  searchApplicationsV2,
  SearchApplicationsV2QueryParamsApi,
  searchApplicationVersionsV2,
  searchConfigurationModulesBffV2,
  SearchConfigurationModulesBffV2QueryParamsApi,
  SearchConfigurationModulesV2MutationRequestApi,
  searchOsV2,
  SearchOsV2MutationRequestApi,
  SearchOsV2QueryParamsApi,
  searchOsVersionsV2,
  SearchOsVersionsV2MutationRequestApi,
  SearchRequestApi,
  searchResourcesV2,
  searchSystemPackagesV2,
  SearchSystemPackagesV2QueryParamsApi,
  searchSystemPackageVersionsV2,
  SystemPackageResourceListResponseV2Api,
  getConfigurationByIdV2,
  modifyConfigurationByIdV2,
  updateConfigurationById,
} from '@ingenico/api-sdk-device-management-private-v2';
import {
  ApplicationOrSystemPackageResourceSearchFront,
  AppOrSystemPackageVersion,
  BasicTemplateResourceBasicFront,
  ConfigurationModuleResourceSearchFront,
  ConfigurationResourceBodyFront,
  ConfigurationResourceReadFront,
  FGBuildByTemplateWithSmartFields,
  OsNameWithId,
  OsVersionWithId,
  RkiKeySetItem,
  TargetEntityTypeEnum,
  UpdateConfigurationFront,
} from '@ingenico/react-configuration-generator';
import type { ModuleInitParams, ParameterSets } from '@ingenico/react-form-builder';
import { toResolvedConfigModuleParameterSet } from './toResolvedConfigModuleParameterSet.util';
import { hasMultiMerchantParameterSets } from '../../../components/configuration-form-builder-wrapper-v2/hasMultiMerchantParameterSets.util';
import { FGLanguagesEnum } from '@ingenico/react-form-generator';
import { CurrentSectionSpiIt } from '@ingenico/react-section-utils';
import { inject, injectable } from 'inversify';
import { forkJoin, map, mergeMap, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { getDataFileResourceIds } from '../../../components/parameter-module/parameter-module-tab/ParameterModuleTab.utils';
import {
  extractTemplateValuesFromConfigurationModulesResponse,
  transformBffApiConfigModuleMinimalIntoFrontResource,
} from '../../../utils/configurationModule.util';
import { mapApplicationListResponseApiOSystemPackageListResponseApi } from '../../api/mappers/applicationOrSystemPackageList.mapper';
import {
  mapTemplatesToFGBuildModel,
  mapTemplatesToFGBuildModelForVirtualDevice,
  mapTemplatesToFormBuilder,
} from '../../api/mappers/applicationOrSystemPackageVersionTemplateById.mapper';
import { mapApplicationOrSystemPackageVersionResourceListResponseV2ApiToFront } from '../../api/mappers/applicationVersion.mapper';
import { mapArtifactsListForFormBuilderResponse } from '../../api/mappers/artifacts/getArtifactsListForFormBuilder.mapper';
import { mapLogoBlobToString } from '../../api/mappers/assets/assets.mapper';
import { mapConfigurationModuleResourceListResponseToFront } from '../../api/mappers/configurationModules.mapper';
import {
  mapConfigurationResourceApiToConfigurationResourceFront,
  mapGetDeployedComputedConfigurationByDeviceIdBffV2QueryResponseApiToConfigurationResourceFront,
} from '../../api/mappers/getConfigurationByIdQuery.mapper';
import { mapConfigurationPutBody } from '../../api/mappers/configurationPutBody.mapper';
import { mapOsListResponseApiToOsVersionAndNameList } from '../../api/mappers/os/osList.mapper';
import { mapOsVersionsByIdForFormBuilder } from '../../api/mappers/os/OsVersionById.mapper';
import {
  mapConfigurationResourceRemoteKeyInjectionApiToNameWithIdList,
  mapEnvironmentResponseToId,
  mapKeySetListToRkiItems,
} from '../../api/mappers/rki.mapper';
import { mapTemplateByIdResponse } from '../../api/mappers/templateById.mapper';
import { mapTemplateResourceListResponseApiToBasicTemplateResourceList } from '../../api/mappers/templatesByApplicationOrSystemPackageVersion.mapper';
import type { DeviceManagementSpiPort } from '../../hexa/ports/spi.model';
import type { ConfigurationModuleParameterSet, TemplateValues } from '../../models/configurationModules.model';
import type { TemplateDetailedResource } from '../../models/templateResource.model';

@injectable()
export class ConfigurationFormGeneratorService {
  private readonly apiSdkClient: CoreClient;

  constructor(@inject(CurrentSectionSpiIt) spi: DeviceManagementSpiPort) {
    this.apiSdkClient = spi.getApiSdkClient();
  }

  getOsList(
    body: SearchOsV2MutationRequestApi,
    params?: SearchOsV2QueryParamsApi,
    isRefetch?: boolean,
    disableCache?: boolean,
  ): Observable<OsNameWithId[]> {
    return this.apiSdkClient
      .request(searchOsV2(body, params), { isRefetch, disableCache })

      .pipe(map((response) => mapOsListResponseApiToOsVersionAndNameList(response)));
  }

  getOsVersionList(
    body: SearchOsVersionsV2MutationRequestApi,
    requestParams?: { readonly disableCache?: boolean },
  ): Observable<OsVersionWithId[]> {
    return this.apiSdkClient
      .requestFullDataOfPaginatedResponse((params) => searchOsVersionsV2(body, params), {
        disableCache: requestParams?.disableCache,
      })
      .pipe(map((osVersions) => mapOsVersionsByIdForFormBuilder(osVersions)));
  }

  getRkiKeySetReference(): Observable<string[]> {
    return this.apiSdkClient
      .request(getKeySetReferenceV2(), {})
      .pipe(map(mapConfigurationResourceRemoteKeyInjectionApiToNameWithIdList));
  }

  getOrganizationKeySetsForProfile(organizationId: string): Observable<RkiKeySetItem[]> {
    return this.apiSdkClient.request(getEnvironmentByOrganizationIdV2(organizationId), {}).pipe(
      switchMap((environment) => {
        const environmentId = mapEnvironmentResponseToId(environment);
        if (!environmentId) {
          return of([]);
        }
        return this.apiSdkClient
          .request(getOrganizationKeySetsV2(organizationId, environmentId), {})
          .pipe(map(mapKeySetListToRkiItems));
      }),
    );
  }

  getTemplatesByAppsOrSystemPackageVersion(
    isApp: boolean,
    versionId: string,
  ): Observable<BasicTemplateResourceBasicFront[]> {
    return this.apiSdkClient
      .requestFullDataOfPaginatedResponse((params) =>
        isApp
          ? getApplicationVersionTemplatesByApplicationVersionIdV2(versionId, params)
          : getSystemPackageVersionTemplatesBySystemPackageVersionIdV2(versionId, params),
      )
      .pipe(map(mapTemplateResourceListResponseApiToBasicTemplateResourceList));
  }

  searchApplicationsOrSystemPackage(
    body: SearchRequestApi,
    isApp: boolean,
    params?: SearchApplicationsV2QueryParamsApi | SearchSystemPackagesV2QueryParamsApi,
  ): Observable<ApplicationOrSystemPackageResourceSearchFront[]> {
    return isApp
      ? this.apiSdkClient
          .request(searchApplicationsV2(body, params as SearchApplicationsV2QueryParamsApi))
          .pipe(
            map((response: ApplicationResourceListResponseV2Api) =>
              mapApplicationListResponseApiOSystemPackageListResponseApi(response.data),
            ),
          )
      : this.apiSdkClient
          .request(searchSystemPackagesV2(body, params as SearchSystemPackagesV2QueryParamsApi))
          .pipe(
            map((response: SystemPackageResourceListResponseV2Api) =>
              mapApplicationListResponseApiOSystemPackageListResponseApi(response.data),
            ),
          );
  }

  downloadSystemPackageOrApplicationLogoByIdV2(assetId: string): Observable<string> {
    return this.apiSdkClient.request(downloadAssetByIdV2(assetId)).pipe(
      mergeMap((response: Blob) => {
        return mapLogoBlobToString(response);
      }),
    );
  }

  searchConfigurationModules(
    body: SearchConfigurationModulesV2MutationRequestApi,
    params?: SearchConfigurationModulesBffV2QueryParamsApi,
  ): Observable<ConfigurationModuleResourceSearchFront[]> {
    return this.apiSdkClient
      .request(searchConfigurationModulesBffV2(body, params))
      .pipe(map((response) => mapConfigurationModuleResourceListResponseToFront(response.data)));
  }

  saveConfiguration(
    configurationId: string,
    body: UpdateConfigurationFront,
    shouldUseModifyEndpoint = false,
  ): Observable<void> {
    if (!shouldUseModifyEndpoint) {
      return this.apiSdkClient.request(updateConfigurationById(configurationId, body)).pipe(map(() => void 0));
    }

    return this.apiSdkClient.request(getConfigurationByIdV2(configurationId), { disableCache: true }).pipe(
      switchMap((currentConfiguration) =>
        this.apiSdkClient.request(
          modifyConfigurationByIdV2(configurationId, mapConfigurationPutBody(currentConfiguration, body)),
        ),
      ),
      map(() => void 0),
    );
  }

  searchAppsOrSystemPackageVersions(isApp: boolean, body: SearchRequestApi): Observable<AppOrSystemPackageVersion[]> {
    return this.apiSdkClient
      .requestFullDataOfPaginatedResponse((params) =>
        isApp ? searchApplicationVersionsV2(body, params) : searchSystemPackageVersionsV2(body, params),
      )
      .pipe(map(mapApplicationOrSystemPackageVersionResourceListResponseV2ApiToFront));
  }

  getConfiguration(
    body: ConfigurationResourceBodyFront,
    params?: { readonly disableCache?: boolean },
  ): Observable<ConfigurationResourceReadFront> {
    return body.entity === TargetEntityTypeEnum.LOGICAL_DEVICE
      ? this.apiSdkClient
          .request(getDeployedComputedConfigurationByDeviceIdBffV2(body.deviceId), {
            disableCache: params?.disableCache,
          })
          .pipe(
            map((response) =>
              mapGetDeployedComputedConfigurationByDeviceIdBffV2QueryResponseApiToConfigurationResourceFront(response),
            ),
          )
      : this.apiSdkClient
          .request(getConfigurationByIdBffV2(body.configurationId), { disableCache: params?.disableCache })
          .pipe(map((response) => mapConfigurationResourceApiToConfigurationResourceFront(response)));
  }

  /**
   * The configuration module "computed" endpoint can return each parameter set in two shapes:
   *  1) with `values` (legacy behavior): everything needed to build the form builder is already there;
   *  2) with `parameterSetSnapshotId` and WITHOUT `values` (new behavior): the values must be fetched from
   *     `/deployment-units/{id}/parameter-sets/snapshots/{snapshotId}/computed`.
   *
   * This method supports both (hybrid behavior, no feature flag) and always returns a module
   * with `values` filled, so the rest of the flow does not need to know the difference.
   */
  private resolveParameterSetSnapshotValues(
    response: ConfigurationModuleSnapshotResourceParameterSetSnapshotReadBffV2Api,
    deploymentUnitId: string,
    isMultiMerchant?: boolean,
    logicalDeviceId?: string,
    templateId?: string,
  ): Observable<
    ConfigurationModuleSnapshotResourceParameterSetSnapshotReadBffV2Api | ConfigurationModuleResourceReadBffV2Api
  > {
    type SnapshotParameterSet = (typeof response.parameterSets)[number];

    // Only calls the new endpoint when there is a snapshot id (and the logical device to query it)
    // and the API did not return the `values` inline.
    const needsSnapshotCall = ({ parameterSetSnapshotId, logicalDeviceId, values }: SnapshotParameterSet) =>
      Boolean(parameterSetSnapshotId && logicalDeviceId && !values);

    const isMultiMerchantCall = isMultiMerchant && logicalDeviceId;
    const responseParameterSets = response.parameterSets.filter(
      ({ logicalDeviceId: parameterSetLogicalDeviceId, templateId: parameterSetTemplateId }) =>
        parameterSetTemplateId === templateId &&
        (!isMultiMerchantCall || parameterSetLogicalDeviceId === logicalDeviceId),
    );

    // Case 1: no parameter set needs a snapshot call -> no extra request is made.
    if (!responseParameterSets.some(needsSnapshotCall)) {
      return of({
        ...response,
        parameterSets: responseParameterSets.map((parameterSet) => toResolvedConfigModuleParameterSet(parameterSet)),
      });
    }

    // Case 2: at least one parameter set has a snapshot id -> fetch its values from the new endpoint
    // (in parallel) and keep untouched those that already came with `values`.
    return forkJoin(
      responseParameterSets.map((parameterSet) =>
        needsSnapshotCall(parameterSet)
          ? this.apiSdkClient
              .request(
                getComputedParameterSetSnapshotByIdBffV2(
                  deploymentUnitId,
                  String(parameterSet.parameterSetSnapshotId),
                  {
                    // The new endpoint requires the logical device id, which comes in the parameter set itself.
                    logicalDeviceId: String(parameterSet.logicalDeviceId),
                  },
                ),
              )
              .pipe(map(({ values }) => toResolvedConfigModuleParameterSet(parameterSet, values)))
          : of(toResolvedConfigModuleParameterSet(parameterSet)),
      ),
    ).pipe(
      map((parameterSets) => {
        const res = {
          ...response,
          parameterSets: [
            ...(isMultiMerchantCall
              ? response.parameterSets
                  .filter(
                    ({ logicalDeviceId: logicalDeviceParameterSetId }) =>
                      logicalDeviceParameterSetId !== logicalDeviceId,
                  )
                  .map((parameterSet) => toResolvedConfigModuleParameterSet(parameterSet))
              : []),
            ...parameterSets,
          ],
        };
        return res;
      }),
    );
  }

  getConfigurationModuleForVirtualDeviceBuilder({
    configurationModuleId,
    isSnapshotId,
    deploymentUnitId,
    logicalDeviceId,
    isDmMultiMerchantEnabled,
    templateId,
  }: {
    configurationModuleId: string;
    isSnapshotId: boolean;
    deploymentUnitId: string;
    logicalDeviceId?: string;
    isDmMultiMerchantEnabled?: boolean;
    templateId?: string;
  }): Observable<
    | ConfigurationModuleSnapshotResourceParameterSetSnapshotReadBffV2Api
    | ConfigurationModuleResourceReadBffV2Api
    | undefined
  > {
    return of(void 0).pipe(
      switchMap(() => {
        if (!configurationModuleId) {
          return of(undefined);
        }
        if (isSnapshotId) {
          return (
            this.apiSdkClient
              .request(getComputedConfigurationModuleSnapshotByIdBffV2(deploymentUnitId, configurationModuleId))
              // After the module computed response, completes `values` via parameter set snapshot when needed.
              .pipe(
                switchMap((response) => {
                  const isMultiMerchant =
                    isDmMultiMerchantEnabled &&
                    hasMultiMerchantParameterSets(response.parameterSets as ParameterSets[], logicalDeviceId);
                  const resolvedParameterSetValues = this.resolveParameterSetSnapshotValues(
                    response,
                    deploymentUnitId,
                    isMultiMerchant,
                    logicalDeviceId,
                    templateId,
                  );
                  return resolvedParameterSetValues;
                }),
                map((response) => response as ConfigurationModuleSnapshotResourceParameterSetSnapshotReadBffV2Api),
              )
          );
        }

        return this.apiSdkClient.request(getConfigurationModuleByIdBffV2(configurationModuleId));
      }),
    );
  }
  private getConfigurationModuleForVirtualDevice(
    configurationModuleId: string,
    isSnapshotId: boolean,
    deploymentUnitId: string,
  ): Observable<TemplateValues> {
    return of(void 0).pipe(
      switchMap(() => {
        if (!configurationModuleId) {
          return of([]);
        }
        if (isSnapshotId) {
          return this.apiSdkClient
            .request(getComputedConfigurationModuleSnapshotByIdBffV2(deploymentUnitId, configurationModuleId))
            .pipe(
              // After the module computed response, completes `values` via parameter set snapshot when needed.
              switchMap((response) => this.resolveParameterSetSnapshotValues(response, deploymentUnitId)),
              map((response) => response as ConfigurationModuleResourceReadBffV2Api),
              map<ConfigurationModuleResourceReadBffV2Api | ConfigurationModuleResourceReadV2Api, TemplateValues>(
                (response: ConfigurationModuleResourceReadBffV2Api | ConfigurationModuleResourceReadV2Api) =>
                  extractTemplateValuesFromConfigurationModulesResponse(
                    transformBffApiConfigModuleMinimalIntoFrontResource(response),
                  ),
              ),
            );
        }

        return this.apiSdkClient
          .request(getConfigurationModuleByIdBffV2(configurationModuleId))
          .pipe(
            map<ConfigurationModuleResourceReadBffV2Api | ConfigurationModuleResourceReadV2Api, TemplateValues>(
              (response: ConfigurationModuleResourceReadBffV2Api | ConfigurationModuleResourceReadV2Api) =>
                extractTemplateValuesFromConfigurationModulesResponse(
                  transformBffApiConfigModuleMinimalIntoFrontResource(response),
                ),
            ),
          );
      }),
    );
  }
  private getConfigurationModule(
    configurationModuleId: string,
    isSnapshotId: boolean,
    _deploymentUnitId: string,
  ): Observable<TemplateValues> {
    return of(void 0).pipe(
      switchMap(() => {
        if (!configurationModuleId) {
          return of([]);
        }
        if (isSnapshotId) {
          return this.apiSdkClient
            .request(getConfigurationModuleSnapshotByIdBffV2(configurationModuleId))
            .pipe(
              map<unknown, TemplateValues>((response: unknown) =>
                extractTemplateValuesFromConfigurationModulesResponse(
                  transformBffApiConfigModuleMinimalIntoFrontResource(
                    response as ConfigurationModuleResourceReadBffV2Api | ConfigurationModuleResourceReadV2Api,
                  ),
                ),
              ),
            );
        }

        return this.apiSdkClient
          .request(getConfigurationModuleByIdBffV2(configurationModuleId))
          .pipe(
            map<ConfigurationModuleResourceReadBffV2Api | ConfigurationModuleResourceReadV2Api, TemplateValues>(
              (response: ConfigurationModuleResourceReadBffV2Api | ConfigurationModuleResourceReadV2Api) =>
                extractTemplateValuesFromConfigurationModulesResponse(
                  transformBffApiConfigModuleMinimalIntoFrontResource(response),
                ),
            ),
          );
      }),
    );
  }
  private getConfigurationModuleBuilder(
    configurationModuleId: string,
    isSnapshotId: boolean,
    _deploymentUnitId: string,
  ): Observable<ConfigurationModuleResourceReadBffV2Api | undefined> {
    return of(void 0).pipe(
      switchMap(() => {
        if (!configurationModuleId) {
          return of(undefined);
        }
        if (isSnapshotId) {
          return this.apiSdkClient
            .request(getConfigurationModuleSnapshotByIdBffV2(configurationModuleId))
            .pipe(map((response) => response as ConfigurationModuleResourceReadBffV2Api));
        }

        return this.apiSdkClient
          .request(getConfigurationModuleByIdBffV2(configurationModuleId))
          .pipe(map((response) => response as ConfigurationModuleResourceReadBffV2Api));
      }),
    );
  }

  getFormBuilderConfigRequests(
    templateId: string,
    configurationModuleId: string,
    isSnapshotId: boolean,
    deploymentUnitId: string,
  ): Observable<{ template: TemplateDetailedResource; parameterSet: ConfigurationModuleParameterSet[] }> {
    return of(void 0).pipe(
      mergeMap(() => this.getTemplate(templateId)),
      mergeMap((template: TemplateDetailedResource) =>
        forkJoin({
          template: of(template),
          parameterSet: this.getConfigurationModuleForVirtualDevice(
            configurationModuleId,
            isSnapshotId,
            deploymentUnitId,
          ),
        }),
      ),
    );
  }
  getFormBuilderConfigRequestsBuilder({
    templateId,
    configurationModuleId,
    isSnapshotId,
    deploymentUnitId,
    logicalDeviceId,
    isDmMultiMerchantEnabled,
  }: {
    templateId: string;
    configurationModuleId: string;
    isSnapshotId: boolean;
    deploymentUnitId: string;
    logicalDeviceId?: string;
    isDmMultiMerchantEnabled?: boolean;
  }): Observable<{
    template: TemplateDetailedResource;
    parameterSet:
      | ConfigurationModuleSnapshotResourceParameterSetSnapshotReadBffV2Api
      | ConfigurationModuleResourceReadBffV2Api
      | undefined;
  }> {
    return of(void 0).pipe(
      mergeMap(() => this.getTemplate(templateId)),
      mergeMap((template: TemplateDetailedResource) =>
        forkJoin({
          template: of(template),
          parameterSet: this.getConfigurationModuleForVirtualDeviceBuilder({
            configurationModuleId,
            isSnapshotId,
            deploymentUnitId,
            logicalDeviceId,
            isDmMultiMerchantEnabled,
            templateId,
          }),
        }),
      ),
    );
  }

  getFormBuilderConfig(
    templateId: string,
    locale: string,
    configurationModuleId: string,
    isSnapshotId: boolean,
    deploymentUnitId: string,
    smartFieldPrefix: string,
  ): Observable<FGBuildByTemplateWithSmartFields> {
    return of(void 0).pipe(
      mergeMap(() => this.getTemplate(templateId)),
      mergeMap((template: TemplateDetailedResource) =>
        forkJoin([of(template), this.getConfigurationModule(configurationModuleId, isSnapshotId, deploymentUnitId)]),
      ),
      map(([template, parameterSet]) =>
        mapTemplatesToFGBuildModel(template, parameterSet, smartFieldPrefix, locale as FGLanguagesEnum),
      ),
    );
  }

  getFormBuilderConfigBuilder(
    templateId: string,
    locale: string,
    configurationModuleId: string,
    isSnapshotId: boolean,
    deploymentUnitId: string,
    _smartFieldPrefix: string,
  ): Observable<ModuleInitParams> {
    return of(void 0).pipe(
      mergeMap(() => this.getTemplate(templateId)),
      mergeMap((template: TemplateDetailedResource) =>
        forkJoin([
          of(template),
          this.getConfigurationModuleBuilder(configurationModuleId, isSnapshotId, deploymentUnitId),
        ]),
      ),
      map(([template, parameterSet]) => mapTemplatesToFormBuilder(template, parameterSet)),
      mergeMap((params) => this.loadFormBuilderDataFiles(params)),
    );
  }

  getFormBuilderConfigForVirtualDevice(
    templateId: string,
    locale: string,
    configurationModuleId: string,
    isSnapshotId: boolean,
    deploymentUnitId: string,
    smartFieldPrefix: string,
  ): Observable<FGBuildByTemplateWithSmartFields> {
    return of(void 0).pipe(
      mergeMap(() =>
        this.getFormBuilderConfigRequests(templateId, configurationModuleId, isSnapshotId, deploymentUnitId),
      ),
      map(({ template, parameterSet }) =>
        mapTemplatesToFGBuildModelForVirtualDevice(template, parameterSet, smartFieldPrefix, locale as FGLanguagesEnum),
      ),
    );
  }

  getFormBuilderConfigForVirtualDeviceBuilder({
    templateId,
    configurationModuleId,
    isSnapshotId,
    deploymentUnitId,
    logicalDeviceId,
    isDmMultiMerchantEnabled,
  }: {
    templateId: string;
    configurationModuleId: string;
    isSnapshotId: boolean;
    deploymentUnitId: string;
    logicalDeviceId?: string;
    isDmMultiMerchantEnabled?: boolean;
  }): Observable<ModuleInitParams> {
    return of(void 0).pipe(
      mergeMap(() =>
        this.getFormBuilderConfigRequestsBuilder({
          templateId,
          configurationModuleId,
          isSnapshotId,
          deploymentUnitId,
          logicalDeviceId,
          isDmMultiMerchantEnabled,
        }),
      ),
      map(({ template, parameterSet }) => mapTemplatesToFormBuilder(template, parameterSet)),
      mergeMap((params) => this.loadFormBuilderDataFiles(params)),
    );
  }

  getFormBuilderConfigForVirtualDeviceBuilderFromExistingTemplate({
    template,
    configurationModuleId,
    isSnapshotId,
    deploymentUnitId,
    logicalDeviceId,
    isDmMultiMerchantEnabled,
  }: {
    template: TemplateDetailedResource;
    configurationModuleId: string;
    isSnapshotId: boolean;
    deploymentUnitId: string;
    logicalDeviceId?: string;
    isDmMultiMerchantEnabled?: boolean;
  }): Observable<ModuleInitParams> {
    return of(void 0).pipe(
      mergeMap(() =>
        forkJoin({
          template: of(template),
          parameterSet: this.getConfigurationModuleForVirtualDeviceBuilder({
            configurationModuleId,
            isSnapshotId,
            deploymentUnitId,
            logicalDeviceId,
            isDmMultiMerchantEnabled,
            templateId: template.id,
          }),
        }),
      ),
      map(({ template, parameterSet }) => mapTemplatesToFormBuilder(template, parameterSet)),
      mergeMap((params) => this.loadFormBuilderDataFiles(params)),
    );
  }

  private loadFormBuilderDataFiles(params: ModuleInitParams): Observable<ModuleInitParams> {
    const resourceIds = getDataFileResourceIds(params.configModule, params.templateToDisplay);
    if (resourceIds.length === 0) {
      return of(params);
    }

    return this.apiSdkClient
      .requestFullDataOfPaginatedResponse((pagination) =>
        searchResourcesV2(
          {
            filters: [{ filterType: 'array', name: 'resource.id', operator: 'in', value: JSON.stringify(resourceIds) }],
            sort: {},
          },
          pagination,
        ),
      )
      .pipe(map((response) => ({ ...params, artifacts: mapArtifactsListForFormBuilderResponse(response) })));
  }

  private getTemplate(templateId: string): Observable<TemplateDetailedResource> {
    return this.apiSdkClient.request(getTemplateByIdV2(templateId)).pipe(map(mapTemplateByIdResponse));
  }
}
